/**
 * Registry of components that are deliberately rendering nothing right now: a
 * Suspense fallback under the navbar, a gate waiting on auth or Statsig, an
 * error boundary parked in its terminal `null` state. The blank-content
 * watchdog attaches the active entries to its report, so a blank page says
 * which component is holding it blank.
 *
 * Entries are coarse labels (`suspense:edge-to-edge-content`,
 * `require-auth:not-logged-in`), never URLs or user data. Register from an
 * effect (see `components/telemetry/BlankMarker`), never during render — SSR
 * shares this module across requests.
 */

const active = new Map<number, string>();
let nextToken = 0;

// A stuck page rarely has more than a handful; the cap keeps a pathological
// tree (one entry per list row) from bloating the RUM payload.
const MAX_REPORTED_REASONS = 20;

/** Returns the unregister function; call it when the component stops rendering nothing. */
export function registerBlankReason(reason: string): () => void {
    const token = nextToken++;
    active.set(token, reason);
    return () => {
        active.delete(token);
    };
}

export function getActiveBlankReasons(): string[] {
    return [...new Set(active.values())].sort().slice(0, MAX_REPORTED_REASONS);
}
